import { useEffect, useMemo, useState } from 'react';
import { SearchType } from '../constants';
import { useInputContext } from '../context/InputContext';
import type { SavedSearch, Suggestion } from '../contracts';
import type { SearchSuggestionWithType } from '../contracts/SearchSuggestion';
import { combineSearches, normalizeSavedSearches } from '../helpers';
import { useSearchBarContext } from '../SearchBarContext';
import { getInitialRecentSearches } from '../suggestions';
import { useSuggestionsContext } from '../SuggestionsContext';
import { highlightText } from '../utils';

const defaultSavedSearches: SavedSearch[] = [];

function useSuggestions() {
  const { suggestionQuery } = useInputContext();
  const { savedSearches: savedSearchesFromProps = defaultSavedSearches } =
    useSearchBarContext();
  const {
    suggestions: { data: suggestedSearches }
  } = useSuggestionsContext();
  const [savedSearches, setSavedSearches] = useState<Suggestion[]>([]);
  const [recentSearches, setRecentSearches] = useState<
    SearchSuggestionWithType[]
  >([]);
  const [keyPressed, setKeyPressed] = useState<string>('');
  const usedArrowKeys = ['ArrowUp', 'ArrowDown'].includes(keyPressed);
  const [filteredSavedSearches, setFilteredSavedSearches] = useState<
    Suggestion[]
  >([]);
  const [filteredRecentSearches, setFilteredRecentSearches] = useState<
    SearchSuggestionWithType[]
  >([]);

  useEffect(() => {
    setSavedSearches(
      normalizeSavedSearches({
        data: savedSearchesFromProps.sort(
          (a, b) => b.savedSearchId - a.savedSearchId
        ),
        searchType: SearchType.SavedSearch
      })
    );
  }, [savedSearchesFromProps]);

  useEffect(() => {
    setRecentSearches(getInitialRecentSearches());
  }, []);

  useEffect(() => {
    if (usedArrowKeys) return;
    setFilteredSavedSearches(prevSavedSearches =>
      // Only filter if saved searches are empty or the last item has been removed
      savedSearches.length || prevSavedSearches.length
        ? savedSearches
            .filter(s =>
              s.text.toLowerCase().includes(suggestionQuery.toLowerCase())
            )
            .map(s => ({
              ...s,
              highlightedText: highlightText(s.text, suggestionQuery)
            }))
        : prevSavedSearches
    );
  }, [savedSearches, suggestionQuery, usedArrowKeys]);

  useEffect(() => {
    if (usedArrowKeys) return;

    setFilteredRecentSearches(prevRecentSearches =>
      // Only filter if recent searches are empty or the last item has been removed
      recentSearches.length || prevRecentSearches.length
        ? recentSearches
            .filter(s =>
              s.text.toLowerCase().includes(suggestionQuery.toLowerCase())
            )
            .map(s => ({
              ...s,
              highlightedText: highlightText(s.text, suggestionQuery)
            }))
        : prevRecentSearches
    );
  }, [recentSearches, suggestionQuery, usedArrowKeys]);

  const searches = useMemo(
    () =>
      combineSearches({
        recentSearches: filteredRecentSearches,
        savedSearches: filteredSavedSearches
      }),
    [filteredRecentSearches, filteredSavedSearches]
  );

  const suggestionResults = useMemo(() => {
    return [...searches, ...suggestedSearches];
  }, [searches, suggestedSearches]);

  return useMemo(
    () => ({
      setKeyPressed,
      setRecentSearches,
      suggestionResults
    }),
    [suggestionResults]
  );
}

export default useSuggestions;
